import { useState, useEffect, useRef, useMemo } from "react";

const PROXY_URL = "api.railubandi.dhanvatix.in/irctc";
const today = new Date().toISOString().split("T")[0];

const classColors = {
  "1A": "#4f46e5", "2A": "#0284c7", "3A": "#0d9488", "SL": "#16a34a",
  "CC": "#ea580c", "2S": "#2563eb", "3E": "#d97706", "FC": "#db2777",
  "GN": "#64748b", "UR": "#64748b",
};

const tierThemes = {
  S: {
    label: "S-Tier: Direct Full Journey",
    sublabel: "Single continuous seat across the entire stretch",
    color: "#e11d48",
    bgSoft: "rgba(225, 29, 72, 0.08)",
    border: "rgba(225, 29, 72, 0.25)",
    badgeBg: "#e11d48",
    badgeText: "#ffffff"
  },
  A: {
    label: "A-Tier: Long-Haul Hops",
    sublabel: "Every seat segment spans ≥ 4 stops",
    color: "#d97706",
    bgSoft: "rgba(217, 119, 6, 0.08)",
    border: "rgba(217, 119, 6, 0.3)",
    badgeBg: "#d97706",
    badgeText: "#ffffff"
  },
  B: {
    label: "B-Tier: Balanced Hops",
    sublabel: "Every seat segment spans ≥ 2 stops",
    color: "#2563eb",
    bgSoft: "rgba(37, 99, 235, 0.08)",
    border: "rgba(37, 99, 235, 0.3)",
    badgeBg: "#2563eb",
    badgeText: "#ffffff"
  },
  C: {
    label: "C-Tier: Short/Flexible Hops",
    sublabel: "All remaining multi-hop configurations (≥ 1 stop)",
    color: "#059669",
    bgSoft: "rgba(5, 150, 105, 0.08)",
    border: "rgba(5, 150, 105, 0.3)",
    badgeBg: "#059669",
    badgeText: "#ffffff"
  }
};

function parseTrainList(data) {
  let items = [];
  if (typeof data === "string") {
    items = data.replace(/^"|"$|\\"/g, "").split('","');
  } else if (Array.isArray(data)) {
    items = data;
  } else if (data && typeof data === "object") {
    items = Object.values(data).flat();
  }

  return items.map(t => {
    if (!t) return null;
    if (typeof t === "string") return t.replace(/"/g, "").trim();
    const num = t.trainNumber || t.trainNo || t.number || t.train_number || "";
    const name = t.trainName || t.name || t.train_name || "";
    if (num && name) return `${num} - ${name}`;
    if (num) return String(num);
    return null;
  }).filter(Boolean);
}

function SearchDropdown({ placeholder, value, onChange, suggestions, onSelect, theme }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { 
      if (ref.current && !ref.current.contains(e.target)) setOpen(false); 
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const safeValue = value || "";
  const filtered = safeValue
    ? suggestions.filter(s => s && s.toLowerCase().includes(safeValue.toLowerCase())).slice(0, 20) 
    : suggestions.slice(0, 20);

  const isDark = theme === "dark";

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        type="text"
        placeholder={placeholder}
        value={safeValue}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        style={{
          width: "100%", boxSizing: "border-box", padding: "9px 12px",
          border: `1px solid ${isDark ? "#334155" : "#cbd5e1"}`, borderRadius: "6px", 
          fontSize: "13px", outline: "none", color: isDark ? "#f8fafc" : "#0f172a",
          background: isDark ? "#0f172a" : "#ffffff"
        }}
      />
      {open && filtered.length > 0 && (
        <div style={{
          position: "absolute", zIndex: 50, top: "calc(100% + 4px)", left: 0, right: 0,
          background: isDark ? "#0f172a" : "#ffffff", border: `1px solid ${isDark ? "#334155" : "#cbd5e1"}`, 
          borderRadius: "6px", maxHeight: "200px", overflowY: "auto", 
          boxShadow: "0 6px 16px rgba(0,0,0,0.15)"
        }}>
          {filtered.map((s, i) => (
            <div
              key={i} onMouseDown={() => { onSelect(s); setOpen(false); }}
              style={{ 
                padding: "8px 12px", cursor: "pointer", fontSize: "13px", 
                borderBottom: i < filtered.length - 1 ? `1px solid ${isDark ? "#1e293b" : "#f1f5f9"}` : "none", 
                color: isDark ? "#e2e8f0" : "#334155" 
              }}
              onMouseEnter={e => e.currentTarget.style.background = isDark ? "#1e293b" : "#f8fafc"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Modal({ isOpen, onClose, tierKey, children, theme }) {
  if (!isOpen || !tierThemes[tierKey]) return null;
  const isDark = theme === "dark";
  const config = tierThemes[tierKey];

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: "rgba(2, 6, 23, 0.7)", zIndex: 1000,
      backdropFilter: "blur(8px)", display: "flex", alignItems: "center",
      justifyContent: "center", padding: "16px", perspective: "1200px"
    }}>
      <style>{`
        @keyframes modalPop3D {
          0% { opacity: 0; transform: rotateX(12deg) scale(0.93) translateY(30px); }
          100% { opacity: 1; transform: rotateX(0deg) scale(1) translateY(0); }
        }
      `}</style>
      <div style={{
        background: isDark ? "#0f172a" : "#ffffff",
        border: `1px solid ${config.border}`,
        borderRadius: "12px", width: "100%", maxWidth: "680px", maxHeight: "88vh",
        display: "flex", flexDirection: "column",
        boxShadow: isDark 
          ? `0 25px 50px -12px rgba(0,0,0,0.8), 0 0 30px ${config.color}22` 
          : `0 25px 50px -12px rgba(0,0,0,0.2), 0 0 25px ${config.color}25`,
        animation: "modalPop3D 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards",
        transformStyle: "preserve-3d"
      }}>
        {/* Header */}
        <div style={{
          padding: "16px 20px", borderBottom: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: config.bgSoft
        }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{
                background: config.badgeBg, color: config.badgeText,
                padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 700
              }}>
                {tierKey}
              </span>
              <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 600, color: isDark ? "#f8fafc" : "#0f172a" }}>
                {config.label}
              </h3>
            </div>
            <p style={{ margin: "4px 0 0", fontSize: "11px", color: isDark ? "#94a3b8" : "#64748b" }}>
              {config.sublabel}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              border: "none", background: "transparent", color: isDark ? "#94a3b8" : "#64748b",
              fontSize: "20px", cursor: "pointer", lineHeight: 1, padding: "4px"
            }}
          >
            &times;
          </button>
        </div>

        {/* Content Body */}
        <div style={{ padding: "20px", overflowY: "auto", flex: 1 }}>
          {children}
        </div>

        {/* Footer */}
        <div style={{
          padding: "12px 20px", borderTop: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
          display: "flex", justifyContent: "flex-end"
        }}>
          <button
            onClick={onClose}
            style={{
              padding: "7px 16px", fontSize: "12px", borderRadius: "6px",
              border: `1px solid ${isDark ? "#334155" : "#cbd5e1"}`,
              background: isDark ? "#1e293b" : "#f1f5f9",
              color: isDark ? "#e2e8f0" : "#334155", cursor: "pointer", fontWeight: 500
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [trainList, setTrainList] = useState([]);
  const [trainQuery, setTrainQuery] = useState("");
  const [selectedTrain, setSelectedTrain] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [boardingStation, setBoardingStation] = useState("");
  const [boardQuery, setBoardQuery] = useState("");
  const [destStation, setDestStation] = useState("");
  const [destQuery, setDestQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState(today);
  const [composition, setComposition] = useState(null);
  const [allBerths, setAllBerths] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [activeTierModal, setActiveTierModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState("light");

  const isDark = theme === "dark";

  useEffect(() => {
    fetch(`${PROXY_URL}/eticketing/trainList`, { headers: { "Accept": "application/json" } })
      .then(res => res.json())
      .then(data => setTrainList(parseTrainList(data)))
      .catch(() => {});
  }, []);

  const handleTrainSelect = async (train) => {
    const num = train.split(" ")[0].replace(/\D/g, "").trim();
    if (!num) return;
    setSelectedTrain(train); setTrainQuery(train); setSchedule(null);
    setBoardingStation(""); setBoardQuery(""); setDestStation(""); setDestQuery("");
    setComposition(null); setAllBerths([]); setSelectedCoach(null);
    try {
      const res = await fetch(`${PROXY_URL}/eticketing/protected/mapps1/trnscheduleenquiry/${num}`);
      setSchedule(await res.json());
    } catch {}
  };

  const fetchComposition = async () => {
    if (!selectedTrain || !boardingStation || !destStation) return;
    const num = selectedTrain.split(" ")[0].replace(/\D/g, "").trim();
    setLoading(true); setComposition(null); setAllBerths([]); setSelectedCoach(null);
    try {
      const res = await fetch(`${PROXY_URL}/online-charts/api/trainComposition`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ trainNo: num, jDate: selectedDate, boardingStation }),
      });
      const data = await res.json();
      setComposition(data);
      
      const firstStation = schedule.stationList[0].stationCode;
      const uniqueClasses = [...new Set(data.cdd.map(c => c.classCode))];
      let gathered = [];
      for (let cls of uniqueClasses) {
        const bRes = await fetch(`${PROXY_URL}/online-charts/api/vacantBerth`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            trainNo: num, boardingStation, remoteStation: firstStation,
            trainSourceStation: firstStation, jDate: selectedDate, cls, chartType: 1
          })
        });
        const bData = await bRes.json();
        if (bData && bData.vbd) gathered = [...gathered, ...bData.vbd];
      }
      setAllBerths(gathered);
    } catch {}
    setLoading(false);
  };

  const stationIndexMap = useMemo(() => {
    const map = {};
    if (schedule) schedule.stationList.forEach((s, i) => { map[s.stationCode] = i; });
    return map;
  }, [schedule]);

  const boardIdx = stationIndexMap[boardingStation] ?? -1;
  const destIdx = stationIndexMap[destStation] ?? 999;

  // ── FIX: Chained Hop Generation with Correct Leg Sequences ────────────────
  const { sTier, aTier, bTier, cTier } = useMemo(() => {
    if (!allBerths.length || boardIdx === -1 || boardIdx >= destIdx) {
      return { sTier: [], aTier: [], bTier: [], cTier: [] };
    }

    // Direct S-Tier
   // Direct S-Tier sorted by coach name, then by seat number
const s = allBerths
  .filter(b => {
    const f = stationIndexMap[b.from];
    const t = stationIndexMap[b.to];
    return f !== undefined && t !== undefined && f <= boardIdx && t >= destIdx;
  })
  .sort((a, b) => {
    // 1. Sort by Coach Name naturally (e.g. B1 before B2, M1 before M2)
    const coachCompare = String(a.coachName).localeCompare(String(b.coachName), undefined, {
      numeric: true,
      sensitivity: "base"
    });
    if (coachCompare !== 0) return coachCompare;

    // 2. Tie-break: Sort by Seat Number numerically
    const seatA = parseInt(String(a.berthNumber).replace(/\D/g, ""), 10) || 0;
    const seatB = parseInt(String(b.berthNumber).replace(/\D/g, ""), 10) || 0;
    return seatA - seatB;
  });

    // Extract valid raw berth segments
    const validBerths = [];
    allBerths.forEach(b => {
      const f = stationIndexMap[b.from];
      const t = stationIndexMap[b.to];
      if (f !== undefined && t !== undefined && t > f && f < destIdx && t > boardIdx) {
        validBerths.push({ ...b, fromIdx: f, toIdx: t });
      }
    });

    // BFS Search that guarantees continuous station connection: Leg 1 (A->B), Leg 2 (B->C)
    const generateChainedRoutes = (minStopsPerLeg) => {
      const routes = [];
      const queue = [{ currentIdx: boardIdx, legs: [] }];
      let iterations = 0;

      while (queue.length > 0 && iterations < 1200) {
        iterations++;
        const { currentIdx, legs } = queue.shift();

        if (currentIdx >= destIdx) {
          if (legs.length > 1) routes.push(legs);
          continue;
        }

        if (legs.length >= 4) continue; // up to 3 transfers max

        // Leg MUST start at or before currentIdx and extend beyond currentIdx
        const candidates = validBerths.filter(b => b.fromIdx <= currentIdx && b.toIdx > currentIdx);

        for (let cand of candidates) {
          // Calculate explicit transfer span for THIS leg
          const legStartIdx = currentIdx;
          const legEndIdx = Math.min(cand.toIdx, destIdx);
          const stopCount = legEndIdx - legStartIdx;

          if (stopCount < minStopsPerLeg) continue;

          // Prevent exact seat re-use within the same route
          const alreadyUsed = legs.some(l => l.coachName === cand.coachName && l.berthNumber === cand.berthNumber);
          if (alreadyUsed) continue;

          const legData = {
            ...cand,
            legStartIdx,
            legEndIdx,
            legStartStation: schedule.stationList[legStartIdx]?.stationCode || cand.from,
            legEndStation: schedule.stationList[legEndIdx]?.stationCode || cand.to,
            stopCount
          };

          queue.push({
            currentIdx: legEndIdx,
            legs: [...legs, legData]
          });
        }
      }

      // Deduplicate identical combinations
      const unique = new Map();
      routes.forEach(r => {
        const key = r.map(l => `${l.coachName}-${l.berthNumber}-${l.legStartIdx}-${l.legEndIdx}`).join("|");
        if (!unique.has(key)) unique.set(key, r);
      });

      // Priority sorting: Same Coach FIRST, then fewest transfers
      return Array.from(unique.values()).sort((a, b) => {
        const aSame = a.every(l => l.coachName === a[0].coachName) ? 0 : 1;
        const bSame = b.every(l => l.coachName === b[0].coachName) ? 0 : 1;
        if (aSame !== bSame) return aSame - bSame;
        return a.length - b.length;
      });
    };

    // A-Tier: Major segments (every seat covers >= 4 stops)
    const a = generateChainedRoutes(4).slice(0, 25);
    const aKeys = new Set(a.map(r => r.map(l => `${l.coachName}-${l.berthNumber}-${l.legStartIdx}`).join("|")));

    // B-Tier: Balanced segments (every seat covers >= 2 stops, not in A)
    const b = generateChainedRoutes(2)
      .filter(r => !aKeys.has(r.map(l => `${l.coachName}-${l.berthNumber}-${l.legStartIdx}`).join("|")))
      .slice(0, 25);
    const bKeys = new Set(b.map(r => r.map(l => `${l.coachName}-${l.berthNumber}-${l.legStartIdx}`).join("|")));

    // C-Tier: Flexible segments (any remaining seat transfers >= 1 stop)
    const c = generateChainedRoutes(1)
      .filter(r => 
        !aKeys.has(r.map(l => `${l.coachName}-${l.berthNumber}-${l.legStartIdx}`).join("|")) &&
        !bKeys.has(r.map(l => `${l.coachName}-${l.berthNumber}-${l.legStartIdx}`).join("|"))
      )
      .slice(0, 25);

    return { sTier: s, aTier: a, bTier: b, cTier: c };
  }, [allBerths, boardIdx, destIdx, stationIndexMap, schedule]);

  const selectedCoachBerths = useMemo(() => {
    if (!selectedCoach || !allBerths.length) return [];
    return allBerths
      .filter(b => b.coachName === selectedCoach)
      .sort((a, b) => (parseInt(a.berthNumber) || 0) - (parseInt(b.berthNumber) || 0));
  }, [selectedCoach, allBerths]);

  const stationSuggestions = schedule ? schedule.stationList.map(s => `${s.stationCode} - ${s.stationName}`) : [];

  const renderRouteCard = (path, idx, config) => {
    const isSameCoach = path.every(l => l.coachName === path[0].coachName);

    return (
      <div key={idx} style={{
        border: `1px solid ${isSameCoach ? config.color : isDark ? "#334155" : "#e2e8f0"}`,
        borderRadius: "8px", padding: "12px 14px",
        background: isDark ? "#1e293b" : "#ffffff",
        boxShadow: isSameCoach ? `0 2px 10px ${config.color}25` : "none"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px", alignItems: "center" }}>
          <span style={{ fontSize: "12px", fontWeight: 700, color: isDark ? "#f8fafc" : "#0f172a" }}>
            Option {idx + 1} ({path.length - 1} seat switch{path.length > 2 ? "es" : ""})
          </span>
          <span style={{
            fontSize: "11px", fontWeight: 600,
            padding: "2px 8px", borderRadius: "4px",
            background: isSameCoach ? config.bgSoft : isDark ? "#334155" : "#f1f5f9",
            color: isSameCoach ? config.color : isDark ? "#94a3b8" : "#64748b"
          }}>
            {isSameCoach ? `★ Same Coach (${path[0].coachName})` : "Multi-Coach Switch"}
          </span>
        </div>

        {/* Correct Leg Sequences (A -> B, B -> C) */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {path.map((leg, lIdx) => (
            <div key={lIdx} style={{
              fontSize: "12px", display: "flex", justifyContent: "space-between",
              alignItems: "center", padding: "6px 10px", borderRadius: "6px",
              background: isDark ? "#0f172a" : "#f8fafc",
              border: `1px solid ${isDark ? "#1e293b" : "#f1f5f9"}`
            }}>
              <div>
                <span style={{ fontWeight: 600, color: config.color }}>Coach {leg.coachName}</span>
                <span style={{ margin: "0 6px", color: isDark ? "#475569" : "#cbd5e1" }}>|</span>
                <span>Seat <b>{leg.berthNumber}</b> {leg.berthCode ? `(${leg.berthCode.trim()})` : ""}</span>
              </div>
              <div style={{ fontSize: "11px", fontWeight: 500, color: isDark ? "#94a3b8" : "#475569" }}>
                <span>{leg.legStartStation} &rarr; {leg.legEndStation}</span>
                <span style={{ marginLeft: "8px", fontWeight: 700, color: isDark ? "#f8fafc" : "#0f172a" }}>
                  ({leg.stopCount} stop{leg.stopCount > 1 ? "s" : ""})
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      minHeight: "100vh", background: isDark ? "#020617" : "#f8fafc",
      color: isDark ? "#f8fafc" : "#0f172a", fontFamily: "system-ui, -apple-system, sans-serif",
      padding: "24px 16px"
    }}>
      <div style={{ maxWidth: "780px", margin: "0 auto" }}>
        
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <div>
            <h1 style={{ fontSize: "18px", fontWeight: 600, margin: "0 0 2px" }}>Train Chart Berth Availability</h1>
            <p style={{ margin: 0, fontSize: "12px", color: isDark ? "#94a3b8" : "#64748b" }}>IRCTC vacant berths and hop route planner</p>
          </div>
          <button
            onClick={() => setTheme(isDark ? "light" : "dark")}
            style={{
              background: isDark ? "#0f172a" : "#ffffff", border: `1px solid ${isDark ? "#334155" : "#cbd5e1"}`,
              color: isDark ? "#f8fafc" : "#0f172a", padding: "6px 12px", borderRadius: "6px",
              fontSize: "12px", cursor: "pointer"
            }}
          >
            {isDark ? "Light Mode" : "Dark Mode"}
          </button>
        </div>

        {/* Inputs */}
        <div style={{
          background: isDark ? "#0f172a" : "#ffffff", border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
          borderRadius: "8px", padding: "16px", marginBottom: "16px"
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={{ display: "block", fontSize: "11px", fontWeight: 500, color: isDark ? "#94a3b8" : "#64748b", marginBottom: "4px" }}>Train</label>
              <SearchDropdown placeholder="Search train name or number" value={trainQuery} onChange={setTrainQuery} suggestions={trainList} onSelect={handleTrainSelect} theme={theme} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "11px", fontWeight: 500, color: isDark ? "#94a3b8" : "#64748b", marginBottom: "4px" }}>Date</label>
              <input
                type="date" value={selectedDate} min={today} onChange={e => setSelectedDate(e.target.value)}
                style={{
                  width: "100%", padding: "8px 12px", borderRadius: "6px",
                  border: `1px solid ${isDark ? "#334155" : "#cbd5e1"}`, boxSizing: "border-box",
                  background: isDark ? "#0f172a" : "#ffffff", color: isDark ? "#f8fafc" : "#0f172a",
                  fontSize: "13px", outline: "none"
                }}
              />
            </div>
          </div>

          {schedule && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "16px" }}>
              <div>
                <label style={{ display: "block", fontSize: "11px", fontWeight: 500, color: isDark ? "#94a3b8" : "#64748b", marginBottom: "4px" }}>From</label>
                <SearchDropdown placeholder="Boarding station" value={boardQuery} onChange={setBoardQuery} suggestions={stationSuggestions} onSelect={s => { setBoardingStation(s.split(" ")[0]); setBoardQuery(s); }} theme={theme} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "11px", fontWeight: 500, color: isDark ? "#94a3b8" : "#64748b", marginBottom: "4px" }}>To</label>
                <SearchDropdown placeholder="Destination station" value={destQuery} onChange={setDestQuery} suggestions={stationSuggestions} onSelect={s => { setDestStation(s.split(" ")[0]); setDestQuery(s); }} theme={theme} />
              </div>
            </div>
          )}

          {boardingStation && destStation && (
            <button
              onClick={fetchComposition} disabled={loading}
              style={{
                padding: "8px 16px", background: "#0284c7", color: "#ffffff", border: "none",
                borderRadius: "6px", fontSize: "13px", fontWeight: 500, cursor: loading ? "not-allowed" : "pointer"
              }}
            >
              {loading ? "Loading Chart..." : "Find Berths & Strategy"}
            </button>
          )}
        </div>

        {/* Strategy Tiers Toolbar */}
        {allBerths.length > 0 && (
          <div style={{
            background: isDark ? "#0f172a" : "#ffffff", border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
            borderRadius: "8px", padding: "14px 16px", marginBottom: "16px",
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px"
          }}>
            <div>
              <span style={{ fontSize: "13px", fontWeight: 600 }}>Journey Tiers</span>
              <span style={{ fontSize: "12px", color: isDark ? "#94a3b8" : "#64748b", marginLeft: "8px" }}>Interactive seat-hop strategies</span>
            </div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <button
                onClick={() => setActiveTierModal("S")}
                style={{
                  padding: "6px 12px", borderRadius: "6px", fontSize: "12px", fontWeight: 600, cursor: "pointer",
                  border: `1px solid ${tierThemes.S.border}`, background: tierThemes.S.bgSoft, color: tierThemes.S.color
                }}
              >
                S-Tier ({sTier.length})
              </button>
              <button
                onClick={() => setActiveTierModal("A")}
                style={{
                  padding: "6px 12px", borderRadius: "6px", fontSize: "12px", fontWeight: 600, cursor: "pointer",
                  border: `1px solid ${tierThemes.A.border}`, background: tierThemes.A.bgSoft, color: tierThemes.A.color
                }}
              >
                A-Tier ({aTier.length})
              </button>
              <button
                onClick={() => setActiveTierModal("B")}
                style={{
                  padding: "6px 12px", borderRadius: "6px", fontSize: "12px", fontWeight: 600, cursor: "pointer",
                  border: `1px solid ${tierThemes.B.border}`, background: tierThemes.B.bgSoft, color: tierThemes.B.color
                }}
              >
                B-Tier ({bTier.length})
              </button>
              <button
                onClick={() => setActiveTierModal("C")}
                style={{
                  padding: "6px 12px", borderRadius: "6px", fontSize: "12px", fontWeight: 600, cursor: "pointer",
                  border: `1px solid ${tierThemes.C.border}`, background: tierThemes.C.bgSoft, color: tierThemes.C.color
                }}
              >
                C-Tier ({cTier.length})
              </button>
            </div>
          </div>
        )}

        {/* Coaches Grid */}
        {composition && (
          <div style={{
            background: isDark ? "#0f172a" : "#ffffff", border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
            borderRadius: "8px", padding: "16px", marginBottom: "16px"
          }}>
            <div style={{ fontSize: "13px", fontWeight: 600, marginBottom: "12px" }}>Coaches Overview</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(70px, 1fr))", gap: "8px" }}>
              {composition.cdd.map(c => {
                const color = classColors[c.classCode] || "#64748b";
                const isSelected = selectedCoach === c.coachName;
                return (
                  <div
                    key={c.coachName}
                    onClick={() => setSelectedCoach(c.coachName)}
                    style={{
                      border: `1px solid ${isSelected ? color : isDark ? "#334155" : "#cbd5e1"}`,
                      background: isSelected ? (isDark ? "#1e293b" : "#f1f5f9") : "transparent",
                      borderRadius: "6px", padding: "8px 6px", textAlign: "center", cursor: "pointer"
                    }}
                  >
                    <div style={{ fontSize: "12px", fontWeight: 600 }}>{c.coachName}</div>
                    <div style={{ fontSize: "10px", color, fontWeight: 500, margin: "2px 0 4px" }}>{c.classCode}</div>
                    <div style={{ fontSize: "11px", color: isDark ? "#94a3b8" : "#64748b" }}>{c.vacantBerths}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Coach Specific Segments */}
        {selectedCoach && (
          <div style={{
            background: isDark ? "#0f172a" : "#ffffff", border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
            borderRadius: "8px", padding: "16px"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <div style={{ fontSize: "13px", fontWeight: 600 }}>Coach {selectedCoach} - Vacant Segments</div>
              <div style={{ fontSize: "11px", color: isDark ? "#94a3b8" : "#64748b" }}>{selectedCoachBerths.length} records</div>
            </div>

            <div style={{
              display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
              gap: "8px", maxHeight: "240px", overflowY: "auto"
            }}>
              {selectedCoachBerths.map((b, i) => (
                <div
                  key={i}
                  style={{
                    border: `1px solid ${isDark ? "#1e293b" : "#f1f5f9"}`,
                    background: isDark ? "#020617" : "#f8fafc",
                    borderRadius: "4px", padding: "6px 8px"
                  }}
                >
                  <div style={{ fontSize: "12px", fontWeight: 600, color: "#0284c7" }}>
                    Seat {b.berthNumber} {b.berthCode ? b.berthCode.trim() : ""}
                  </div>
                  <div style={{ fontSize: "11px", color: isDark ? "#94a3b8" : "#64748b" }}>
                    {b.from} &rarr; {b.to}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* S-TIER MODAL */}
        <Modal
          isOpen={activeTierModal === "S"}
          onClose={() => setActiveTierModal(null)}
          tierKey="S"
          theme={theme}
        >
          {sTier.length === 0 ? (
            <div style={{ fontSize: "13px", color: isDark ? "#94a3b8" : "#64748b" }}>No direct single-seat full journeys available.</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: "10px" }}>
              {sTier.map((b, i) => (
                <div key={i} style={{
                  padding: "10px 8px", borderRadius: "8px", border: `1px solid ${tierThemes.S.border}`,
                  background: isDark ? "#1e293b" : "#ffffff", textAlign: "center"
                }}>
                  <div style={{ fontSize: "11px", color: isDark ? "#94a3b8" : "#64748b", marginBottom: "2px" }}>Coach {b.coachName}</div>
                  <div style={{ fontSize: "14px", color: tierThemes.S.color, fontWeight: 700 }}>Seat {b.berthNumber} {b.berthCode || ""}</div>
                </div>
              ))}
            </div>
          )}
        </Modal>

        {/* A-TIER MODAL */}
        <Modal
          isOpen={activeTierModal === "A"}
          onClose={() => setActiveTierModal(null)}
          tierKey="A"
          theme={theme}
        >
          {aTier.length === 0 ? (
            <div style={{ fontSize: "13px", color: isDark ? "#94a3b8" : "#64748b" }}>No combinations where each seat spans ≥ 4 stops.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {aTier.map((path, idx) => renderRouteCard(path, idx, tierThemes.A))}
            </div>
          )}
        </Modal>

        {/* B-TIER MODAL */}
        <Modal
          isOpen={activeTierModal === "B"}
          onClose={() => setActiveTierModal(null)}
          tierKey="B"
          theme={theme}
        >
          {bTier.length === 0 ? (
            <div style={{ fontSize: "13px", color: isDark ? "#94a3b8" : "#64748b" }}>No combinations where each seat spans ≥ 2 stops.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {bTier.map((path, idx) => renderRouteCard(path, idx, tierThemes.B))}
            </div>
          )}
        </Modal>

        {/* C-TIER MODAL */}
        <Modal
          isOpen={activeTierModal === "C"}
          onClose={() => setActiveTierModal(null)}
          tierKey="C"
          theme={theme}
        >
          {cTier.length === 0 ? (
            <div style={{ fontSize: "13px", color: isDark ? "#94a3b8" : "#64748b" }}>No flexible multi-hop combinations found.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {cTier.map((path, idx) => renderRouteCard(path, idx, tierThemes.C))}
            </div>
          )}
        </Modal>

      </div>
    </div>
  );
}